import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
    HiOutlineBookOpen,
    HiOutlinePhone,
    HiOutlineTag,
    HiOutlineBell,
    HiOutlineCalendar,
    HiOutlineHome,
    HiOutlineChartBar,
    HiChevronLeft,
    HiChevronRight,
    HiPlus,
} from 'react-icons/hi';
import { BiGridAlt } from 'react-icons/bi';
import { getMyStores, getStoreInfo } from '../../api/stores';

const getStoreItems = (uuid, isReservationEnabled) => [
    { icon: HiOutlineHome,         label: '스토어 기본 / 운영시간', path: `/stores/${uuid}/store-info`,    match: (p) => p.includes('/store-info') },
    { icon: HiOutlineTag,          label: '서비스 금액 / 메뉴',  path: `/stores/${uuid}/menus`,           match: (p) => p.includes('/menus') },
    { icon: HiOutlineBookOpen,     label: '자주 묻는 질문',       path: `/stores/${uuid}/knowledge-bases`, match: (p) => p.includes('/knowledge-bases') },
    ...(isReservationEnabled ? [
        { icon: HiOutlineCalendar, label: '예약 채널 관리',        path: `/stores/${uuid}/channels`,    match: (p) => p.endsWith('/channels') },
    ] : []),
    { icon: HiOutlineBell,         label: '요약 / 알림 설정',    path: `/stores/${uuid}/notifications`,   match: (p) => p.includes('/notifications') },
    { icon: HiOutlinePhone,        label: '통화 기록',           path: `/stores/${uuid}/call-logs`,       match: (p) => p.includes('/call-logs') },
];

const Sidebar = ({ collapsed, onToggle }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const storeMatch = pathname.match(/^\/stores\/([^/]+)/);
    const storeUuid = storeMatch?.[1];

    const [stores, setStores] = useState([]);
    const [storeName, setStoreName] = useState(null);
    const [storeLogo, setStoreLogo] = useState(null);
    const [isReservationEnabled, setIsReservationEnabled] = useState(false);
    const [파트너모달열림, set파트너모달열림] = useState(false);

    useEffect(() => {
        if (storeUuid) return;
        getMyStores().then(setStores).catch(() => {});
    }, [storeUuid]);

    useEffect(() => {
        if (!storeUuid) { setStoreName(null); setIsReservationEnabled(false); return; }
        getStoreInfo(storeUuid)
            .then((data) => {
                const store = data?.store ?? data;
                setStoreName(store?.name ?? '');
                setStoreLogo(store?.logo_image_url ?? null);
                setIsReservationEnabled(!!store?.is_reservation_enabled);
            })
            .catch(() => {});
    }, [storeUuid]);

    const isGlobal = !storeUuid;
    const storeItems = storeUuid ? getStoreItems(storeUuid, isReservationEnabled) : [];

    return (
        <>
        {파트너모달열림 && (
            <ModalOverlay onClick={() => set파트너모달열림(false)}>
                <ModalBox onClick={(e) => e.stopPropagation()}>
                    <ModalEmoji>🚧</ModalEmoji>
                    <ModalTitle>아직은 안 돼요!</ModalTitle>
                    <ModalDesc>현재는 테스트 파트너 기간이라<br />스토어 추가를 지원하지 않아요!</ModalDesc>
                    <ModalClose onClick={() => set파트너모달열림(false)}>확인</ModalClose>
                </ModalBox>
            </ModalOverlay>
        )}
        <Nav $collapsed={collapsed}>
            {isGlobal ? (
                <>
                <MenuList style={{ flex: 'none' }}>
                    <MenuItem
                        $active={pathname === '/overview'}
                        $collapsed={collapsed}
                        onClick={() => navigate('/overview')}
                        title={collapsed ? '이달의 현황' : undefined}
                    >
                        <HiOutlineChartBar size={22} />
                        {!collapsed && <span>이달의 현황</span>}
                    </MenuItem>
                    <MenuItem
                        $active={pathname === '/stores' || pathname === '/dashboard'}
                        $collapsed={collapsed}
                        onClick={() => navigate('/stores')}
                        title={collapsed ? '스토어 목록' : undefined}
                    >
                        <BiGridAlt size={22} />
                        {!collapsed && <span>스토어 목록</span>}
                    </MenuItem>
                </MenuList>
                <Divider />
                <MenuList>
                    {stores.map((store) => {
                        const active = pathname === `/stores/${store.uuid}/channels` ||
                            pathname.startsWith(`/stores/${store.uuid}/`);
                        return (
                            <MenuItem
                                key={store.uuid}
                                $active={active}
                                $collapsed={collapsed}
                                onClick={() => navigate(`/stores/${store.uuid}/channels`)}
                                title={collapsed ? store.name : undefined}
                            >
                                <SidebarStoreIcon src={store.logo_image_url} alt="" />
                                {!collapsed && <span>{store.name}</span>}
                            </MenuItem>
                        );
                    })}
                    {stores.length === 0 && !collapsed && (
                        <EmptyText>스토어가 없습니다</EmptyText>
                    )}
                    <CreateButton $collapsed={collapsed} onClick={() => set파트너모달열림(true)}>
                        <HiPlus size={18} />
                        {!collapsed && <CreateLabel>새로 만들기</CreateLabel>}
                    </CreateButton>
                </MenuList>
                </>
            ) : (
                <>
                    <StoreHeader
                        $collapsed={collapsed}
                        onClick={() => navigate('/stores')}
                        title={collapsed ? storeName : undefined}
                    >
                        <SidebarStoreIcon src={storeLogo} alt="" />
                        {!collapsed && (
                            <StoreName>{storeName ?? '...'}</StoreName>
                        )}
                    </StoreHeader>
                    <Divider />

                    <MenuList>
                        {storeItems.map(({ icon: Icon, label, path, match }) => {
                            const active = match(pathname);
                            return (
                                <MenuItem
                                    key={label}
                                    $active={active}
                                    $disabled={!path}
                                    $collapsed={collapsed}
                                    onClick={() => path && navigate(path)}
                                    title={collapsed ? label : undefined}
                                >
                                    <Icon size={22} />
                                    {!collapsed && <span>{label}</span>}
                                </MenuItem>
                            );
                        })}
                    </MenuList>
                </>
            )}

            <ToggleButton onClick={onToggle} title={collapsed ? '펼치기' : '접기'}>
                {collapsed ? <HiChevronRight size={22} /> : <HiChevronLeft size={22} />}
            </ToggleButton>
        </Nav>
        </>
    );
};

const Nav = styled.nav`
    position: fixed;
    top: 56px;
    left: 0;
    width: ${({ $collapsed }) => $collapsed ? '60px' : '200px'};
    height: calc(100dvh - 56px);
    background: #ffffff;
    border-right: 1px solid #eeeeee;
    display: flex;
    flex-direction: column;
    padding: 16px 0 12px;
    z-index: 90;
    box-sizing: border-box;
    transition: width 0.2s ease;
    overflow: hidden;
`;

const CreateButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 8px 0;
    padding: ${({ $collapsed }) => $collapsed ? '9px' : '9px 12px'};
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #ffffff;
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #333333;
    cursor: pointer;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    white-space: nowrap;
    transition: border-color 0.12s, background 0.12s;

    &:hover {
        border-color: #487BFD;
        background: #f5f8ff;
        color: #487BFD;
    }
`;

const CreateLabel = styled.span`
    flex: 1;
`;



const StoreHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 8px 0;
    padding: ${({ $collapsed }) => $collapsed ? '10px' : '10px 14px'};
    border-radius: 8px;
    background: #eef2ff;
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #487BFD;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #e0e8ff; }
`;


const StoreName = styled.span`
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #487BFD;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MenuList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
`;

const MenuItem = styled.li`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 8px;
    padding: ${({ $collapsed }) => $collapsed ? '10px' : '10px 14px'};
    border-radius: 8px;
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 500;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
    color: ${({ $active }) => $active ? '#487BFD' : '#555555'};
    background: ${({ $active }) => $active ? '#eef2ff' : 'transparent'};
    transition: background 0.12s, color 0.12s;
    white-space: nowrap;

    &:hover {
        background: ${({ $active, $disabled }) =>
            $active ? '#eef2ff' : $disabled ? 'transparent' : '#f5f5f5'};
        color: ${({ $active, $disabled }) =>
            $active ? '#487BFD' : $disabled ? '#555555' : '#111111'};
    }
`;

const Divider = styled.div`
    height: 1px;
    background: #eeeeee;
    margin: 8px 8px 4px;
`;

const SidebarStoreIcon = styled.img`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: #eeeeee;
    border: 1px solid #e0e0e0;
`;

const EmptyText = styled.span`
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 13px;
    color: #cccccc;
    padding: 8px 16px;
`;

const ToggleButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-end;
    margin-right: 8px;
    padding: 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #aaaaaa;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;

    &:hover { background: #f5f5f5; color: #333333; }
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 500;
`;

const ModalBox = styled.div`
    background: #ffffff;
    border-radius: 20px;
    padding: 36px 32px 28px;
    width: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
`;

const ModalEmoji = styled.div`
    font-size: 40px;
    margin-bottom: 12px;
`;

const ModalTitle = styled.div`
    font-family: Pretendard, sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #111111;
    margin-bottom: 10px;
`;

const ModalDesc = styled.div`
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #777777;
    line-height: 160%;
    margin-bottom: 24px;
`;

const ModalClose = styled.button`
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 12px;
    background: #487BFD;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #3568e8; }
`;

export default Sidebar;
